/**
 * The "where should it run" step of the add-project flow: the rows it shows,
 * and the answer a chosen row carries into the rest of the import.
 *
 * Its own module because the rows are the one place Forge decides which
 * Environments a person may put a workspace on, and that decision changed when
 * CMMD re-keyed Environments to (organization, user) on 2026-08-28.
 */
import type { SavedEnvironmentKind } from "@cmmd-forge/contracts";
import { EnvironmentId } from "@cmmd-forge/contracts";
import {
  toProjectImportDestination,
  type ProjectImportDestination,
  type ProjectImportDestinationKind,
} from "../projectImportStore";
import type { ForgeRuntimeSurface } from "../environments/runtimeSurface";
import { classifyEnvironmentId } from "../environments/environmentIdClassification";
import { localDeviceLabel } from "../lib/utils";

/**
 * One row of the "where should it run" step: a real, named environment the
 * workspace can live in. Forge derives the local machine's name server-side
 * (ServerEnvironmentLabel resolves the friendly host label, e.g. "Alex's
 * MacBook Pro"), so this names the device instead of saying "This Mac". That
 * is correct on Windows and Linux too.
 */
export interface ProjectImportEnvironmentDescriptor {
  readonly id: string;
  readonly destination: ProjectImportDestinationKind;
  /** Environment to add the project into; null means "provision a new one". */
  readonly environmentId: string | null;
  readonly value: string;
  readonly title: string;
  readonly description: string;
  readonly searchTerms: readonly string[];
}

export interface ProjectImportEnvironmentInput {
  readonly surface?: ForgeRuntimeSurface;
  /** Primary environment: the machine this Forge client is talking to. */
  readonly primary?: { readonly environmentId: string; readonly label: string } | null;
  /** Other environments in the saved registry. */
  readonly saved?: ReadonlyArray<{
    readonly environmentId: string;
    readonly label: string;
    /** Credential Vault records host no projects, so they are never targets. */
    readonly kind?: SavedEnvironmentKind | undefined;
  }>;
  /** Platform string, for the fallback label when the server sent no name. */
  readonly platform?: string;
}

/** Synthetic row id: not a registered environment, a request to make one. */
const NEW_FORGE_CLOUD_ENVIRONMENT_ID = "new-forge-cloud";
/** Used when a saved cloud record arrived without a usable label. */
const FORGE_CLOUD_FALLBACK_LABEL = "Forge Cloud";

type SavedRecord = NonNullable<ProjectImportEnvironmentInput["saved"]>[number];

function isCloudEnvironmentRecord(record: SavedRecord): boolean {
  return classifyEnvironmentId(record.environmentId).isCloud;
}

function primaryDeviceRow(
  input: ProjectImportEnvironmentInput,
): ProjectImportEnvironmentDescriptor {
  const label = input.primary?.label?.trim() ?? "";
  const deviceLabel = localDeviceLabel(input.platform);
  return {
    id: "primary",
    destination: "desktop-local",
    environmentId: input.primary?.environmentId ?? null,
    value: "action:add-project:environment:primary",
    // The server names the machine; the platform noun is only a fallback for
    // a client that has not read a descriptor yet.
    title: label || deviceLabel,
    description: "This device",
    searchTerms: [
      label.toLowerCase(),
      deviceLabel.toLowerCase(),
      "this device",
      "this mac",
      "this pc",
      "local",
      "desktop",
      "here",
    ].filter((term) => term.length > 0),
  };
}

function localMachineRow(record: SavedRecord): ProjectImportEnvironmentDescriptor {
  return {
    id: record.environmentId,
    destination: "desktop-local",
    environmentId: record.environmentId,
    value: `action:add-project:environment:${record.environmentId}`,
    title: record.label,
    // t3code shows the raw environment id here; it is the only stable way to
    // tell two machines with the same friendly name apart.
    description: record.environmentId,
    searchTerms: [record.label.toLowerCase(), record.environmentId, "machine", "remote"],
  };
}

function cloudEnvironmentRow(record: SavedRecord): ProjectImportEnvironmentDescriptor {
  return {
    id: record.environmentId,
    destination: "forge-cloud",
    environmentId: record.environmentId,
    value: `action:add-project:environment:${record.environmentId}`,
    // The label was derived from the CMMD user when the record was written,
    // so it already reads as this person's Environment.
    title: record.label.trim() || FORGE_CLOUD_FALLBACK_LABEL,
    description: "Runs in the Forge Cloud",
    searchTerms: [record.label.toLowerCase(), record.environmentId, "forge cloud", "cloud", "vm"],
  };
}

/**
 * One Environment per person is what CMMD enforces, so this row asks for the
 * first one rather than an extra one.
 */
function newForgeCloudRow(): ProjectImportEnvironmentDescriptor {
  return {
    id: NEW_FORGE_CLOUD_ENVIRONMENT_ID,
    destination: "forge-cloud",
    environmentId: null,
    value: `action:add-project:environment:${NEW_FORGE_CLOUD_ENVIRONMENT_ID}`,
    title: "New Forge Cloud VM",
    description: "Runs in the Forge Cloud",
    searchTerms: ["forge cloud", "cloud", "vm", "new", "remote", "sprite"],
  };
}

/**
 * Rows for the environment step, in the order they are shown: this device
 * first, then any other connected machines by name, then the cloud Environment
 * this person owns.
 *
 * Three rules shape the list and none of them is cosmetic:
 * - The saved cloud record is a real row carrying its own environmentId. CMMD
 *   keys an Environment to (organization, user), so it is the one Environment
 *   this seat owns and every project of theirs can live on it.
 * - The Credential Vault holds provider credentials and hosts no projects, so
 *   it is never an import target.
 * - On the cloud-official surface there is no local machine to add into, so
 *   only the cloud row survives, and a one-row step is skipped by the caller.
 */
export function buildProjectImportEnvironmentDescriptors(
  input: ProjectImportEnvironmentInput = {},
): ProjectImportEnvironmentDescriptor[] {
  const isCloudOnly = input.surface === "cloud-official";
  const importable = (input.saved ?? []).filter((record) => record.kind !== "auth");
  const cloudRecords = importable.filter(isCloudEnvironmentRecord);
  // The primary is whichever server this client is talking to, identified by
  // that server's own uuid. A cloud record is either the legacy
  // `cmmd-runtime:{id}` shape or a durable `env_…` id, neither of which a
  // primary's uuid can collide with, which is why only the local rows
  // deduplicate.
  const primaryEnvironmentId = input.primary?.environmentId ?? null;
  const localRows = isCloudOnly
    ? []
    : [
        primaryDeviceRow(input),
        ...importable
          .filter(
            (record) =>
              !isCloudEnvironmentRecord(record) && record.environmentId !== primaryEnvironmentId,
          )
          .map(localMachineRow),
      ];
  const cloudRows = cloudRecords.map(cloudEnvironmentRow);
  return [...localRows, ...cloudRows, ...(cloudRecords.length === 0 ? [newForgeCloudRow()] : [])];
}

/** The answer a "where" row stands for, ready to carry through the import. */
export function importDestinationForEnvironmentRow(
  row: ProjectImportEnvironmentDescriptor,
): ProjectImportDestination {
  return toProjectImportDestination(
    row.destination,
    row.environmentId ? EnvironmentId.make(row.environmentId) : null,
  );
}

/** Heading for the source list, named after the environment already chosen. */
export function addProjectSourceGroupLabel(
  destination?: ProjectImportDestinationKind | null,
): string {
  if (destination === "forge-cloud") return "Cloud sources";
  return destination === "desktop-local" ? "Local sources" : "Sources";
}
